import Controller from '@ember/controller';
import { action } from '@ember/object';
import { service } from '@ember/service';
import { tracked } from '@glimmer/tracking';
import type AbilitiesService from 'ember-can/services/abilities';
import type { DiffGroup, DiffItem } from 'garaje/components/hris-dashboard/save-confirm-modal';
import config from 'garaje/config/environment';
import { ForbiddenError } from 'garaje/services/ajax/errors';
import type AjaxFetchService from 'garaje/services/ajax-fetch';
import type AuthzService from 'garaje/services/authz';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type StateService from 'garaje/services/state';
import { Permission, PermissionType } from 'garaje/utils/ui-permissions';

interface StepDef {
  id: string;
  title: string;
  desc: string;
  icon: string;
  tone: 'red' | 'papaya' | 'gray';
  kind: 'perSystem' | 'org';
  sysLabel?: string;
  nestedIcon?: boolean;
}

interface LocationDef {
  id: string;
  tag: string;
  acs: string[];
}

type Mode = 'live' | 'dryrun' | 'disabled';

interface RoutineState {
  acs: Record<string, Mode>;
  blocklist: Mode;
  forceSignout: Mode;
}

const STEPS: StepDef[] = [
  {
    id: 'acs',
    title: 'Deprovision ACS credentials',
    desc: 'Remove employee access in access control systems.',
    icon: 'suspicious-activity-hris',
    tone: 'red',
    kind: 'perSystem',
    sysLabel: 'Access control systems',
  },
  {
    id: 'blocklist',
    title: 'Add to visitor blocklist',
    desc: 'Prevent deactivated employees from visiting.',
    icon: 'dash-in-circle-hris',
    tone: 'red',
    kind: 'org',
  },
  {
    id: 'forceSignout',
    title: 'Force sign-out',
    desc: 'Force the employee to sign out of all locations.',
    icon: 'door-hris',
    tone: 'papaya',
    kind: 'org',
  },
];

const GRACE_UNIT_HOURS: Record<string, number> = {
  minutes: 1 / 60,
  hours: 1,
  days: 24,
};

const GRACE_MAX_HOURS = 5 * 24;

const EMPTY_STATE: RoutineState = {
  acs: {},
  blocklist: 'disabled',
  forceSignout: 'disabled',
};

function toUiMode(apiMode: string): Mode {
  if (apiMode === 'LIVE') return 'live';
  if (apiMode === 'SIMULATE') return 'dryrun';
  return 'disabled';
}

function toApiMode(mode: Mode): string {
  if (mode === 'live') return 'LIVE';
  if (mode === 'dryrun') return 'SIMULATE';
  return 'DISABLED';
}

function cloneState(s: RoutineState): RoutineState {
  return { ...s, acs: { ...s.acs } };
}

export default class LocationOverviewHrisDashboardController extends Controller {
  @service declare abilities: AbilitiesService;
  @service declare ajaxFetch: AjaxFetchService;
  @service declare authz: AuthzService;
  @service declare flashMessages: FlashMessagesService;
  @service declare state: StateService;

  @tracked eventId = 'terminate';
  @tracked gracePeriodQty = 24;
  @tracked gracePeriodUnit: 'minutes' | 'hours' | 'days' = 'hours';
  @tracked routineState: RoutineState = cloneState(EMPTY_STATE);
  @tracked locations: LocationDef[] = [];
  @tracked showSaveConfirmModal = false;
  @tracked pendingDiffGroups: DiffGroup[] = [];

  private _savedGraceQty = 24;
  private _savedGraceUnit: 'minutes' | 'hours' | 'days' = 'hours';
  private _savedState: RoutineState = cloneState(EMPTY_STATE);

  loadFromSnapshot(snapshot: unknown, unauthorized = false): void {
    if (unauthorized) {
      this.flashMessages.showAndHideFlash('error', 'You are not authorized to view this configuration.');
      return;
    }

    if (!snapshot) return;

    const snap = snapshot as {
      config: {
        grace_period_seconds: number;
        steps: Array<{
          step_type: string;
          mode?: string;
          per_integration_location?: Array<{
            integration_type: string;
            location_id: number;
            location_name?: string;
            mode: string;
          }>;
        }>;
      };
    };

    const secs = snap.config.grace_period_seconds;
    if (secs % 86400 === 0) {
      this.gracePeriodQty = secs / 86400;
      this.gracePeriodUnit = 'days';
    } else if (secs % 3600 === 0) {
      this.gracePeriodQty = secs / 3600;
      this.gracePeriodUnit = 'hours';
    } else {
      this.gracePeriodQty = Math.round(secs / 60);
      this.gracePeriodUnit = 'minutes';
    }

    const acsStep = snap.config.steps.find((s) => s.step_type === 'LOCATION_ACS_DEPROVISION');
    const blocklistStep = snap.config.steps.find((s) => s.step_type === 'COMPANY_BLOCKLIST_ADD');
    const forceSignoutStep = snap.config.steps.find((s) => s.step_type === 'COMPANY_FORCE_SIGNOUT');

    const locMap = new Map<string, LocationDef>();
    acsStep?.per_integration_location?.forEach((item) => {
      const locId = String(item.location_id);
      // eslint-disable-next-line ember/use-ember-get-and-set
      if (!locMap.has(locId)) locMap.set(locId, { id: locId, tag: item.location_name ?? locId, acs: [] });
      // eslint-disable-next-line ember/use-ember-get-and-set
      locMap.get(locId)!.acs.push(item.integration_type);
    });
    this.locations = [...locMap.values()];

    const acs: Record<string, Mode> = {};
    acsStep?.per_integration_location?.forEach((item) => {
      acs[`${item.location_id}|${item.integration_type}`] = toUiMode(item.mode);
    });

    this.routineState = {
      acs,
      blocklist: toUiMode(blocklistStep?.mode ?? 'DISABLED'),
      forceSignout: toUiMode(forceSignoutStep?.mode ?? 'DISABLED'),
    };

    this._savedGraceQty = this.gracePeriodQty;
    this._savedGraceUnit = this.gracePeriodUnit;
    this._savedState = cloneState(this.routineState);
  }

  get isGlobal(): boolean {
    return this.abilities.can('manage all hris-dashboard');
  }

  get isReadOnly(): boolean {
    return !this.isGlobal && this.abilities.cannot('manage hris-dashboard');
  }

  private canManageLocation(locationId: string): boolean {
    if (this.isGlobal) return true;
    return this.authz.hasPermission(PermissionType.LOCATION, locationId, Permission.HRIS_LIFECYCLE_ROUTINE_MANAGE);
  }

  get graceInvalid(): boolean {
    const hours = this.gracePeriodQty * (GRACE_UNIT_HOURS[this.gracePeriodUnit] ?? 1);
    return hours > GRACE_MAX_HOURS;
  }

  get graceSummary(): string {
    return `${this.gracePeriodQty} ${this.gracePeriodUnit}`;
  }

  get isDirty(): boolean {
    if (this.gracePeriodQty !== this._savedGraceQty || this.gracePeriodUnit !== this._savedGraceUnit) {
      return true;
    }
    const s = this.routineState;
    const b = this._savedState;
    if (s.blocklist !== b.blocklist || s.forceSignout !== b.forceSignout) return true;
    for (const k of Object.keys(s.acs)) {
      if (s.acs[k] !== b.acs[k]) return true;
    }
    return false;
  }

  readonly graceUnitOptions: Array<{ value: string; label: string }> = [
    { value: 'minutes', label: 'Minutes' },
    { value: 'hours', label: 'Hours' },
    { value: 'days', label: 'Days' },
  ];

  get selectedGraceUnit(): { value: string; label: string } | undefined {
    return this.graceUnitOptions.find((o) => o.value === this.gracePeriodUnit);
  }

  readonly modeOptions: Array<{ value: Mode; label: string }> = [
    { value: 'live', label: 'Live' },
    { value: 'dryrun', label: 'Simulate' },
    { value: 'disabled', label: 'Disabled' },
  ];

  private modeLabelFor(mode: Mode): string {
    return mode === 'live' ? 'Live' : mode === 'dryrun' ? 'Simulate' : 'Disabled';
  }

  private modePillClass(mode: Mode): string {
    if (mode === 'live') return 'bg-cilantro-10 text-cilantro-60';
    if (mode === 'dryrun') return 'bg-gem-10 text-gem-60';
    return 'bg-gray-100 text-carbon-40';
  }

  private diffItem(label: string, fromMode: Mode, toMode: Mode): DiffItem {
    return {
      label,
      from: this.modeLabelFor(fromMode),
      fromPillClass: this.modePillClass(fromMode),
      to: this.modeLabelFor(toMode),
      toPillClass: this.modePillClass(toMode),
    };
  }

  private computeGoingLive(): DiffGroup[] {
    const cur = this.routineState;
    const base = this._savedState;
    const goingLive = (c: Mode, b: Mode): boolean => b !== 'live' && c === 'live';
    const byLoc: Record<string, DiffItem[]> = {};

    for (const loc of this.locations) {
      for (const vendor of loc.acs) {
        const k = `${loc.id}|${vendor}`;
        const c = cur.acs[k] ?? 'disabled';
        const b = base.acs[k] ?? 'disabled';
        if (goingLive(c, b)) {
          (byLoc[loc.id] ??= []).push(this.diffItem(`${vendor} — deprovision ACS credentials`, b, c));
        }
      }
    }

    const orgItems: DiffItem[] = [];
    for (const id of ['blocklist', 'forceSignout'] as const) {
      const c = cur[id];
      const b = base[id];
      if (goingLive(c, b)) {
        const step = STEPS.find((s) => s.id === id)!;
        orgItems.push(this.diffItem(step.title, b, c));
      }
    }

    const groups: DiffGroup[] = [];
    for (const loc of this.locations) {
      if (byLoc[loc.id]) groups.push({ heading: loc.tag, items: byLoc[loc.id]! });
    }
    if (orgItems.length) groups.push({ heading: 'Company-wide', items: orgItems });
    return groups;
  }

  private selectedOptionFor(mode: Mode): { value: Mode; label: string } | undefined {
    return this.modeOptions.find((o) => o.value === mode);
  }

  private iconBgFor(tone: StepDef['tone']): string {
    if (tone === 'papaya') return 'bg-papaya-10 text-papaya-40';
    if (tone === 'red') return 'bg-red-10 text-red-50';
    return 'bg-gray-100 text-carbon-40';
  }

  get stepsWithSubItems(): Array<
    StepDef & {
      iconBg: string;
      isLocked: boolean;
      subItems?: Array<{
        key: string;
        label: string;
        locationPrefix?: string;
        isLast: boolean;
        isLocked: boolean;
        currentMode: Mode;
        modeLabel: string;
        selectedModeOption: { value: Mode; label: string } | undefined;
      }>;
      currentMode?: Mode;
      modeLabel?: string;
      selectedModeOption?: { value: Mode; label: string } | undefined;
    }
  > {
    return STEPS.map((step) => {
      if (step.kind === 'perSystem') {
        const items = this.locations.flatMap((loc) =>
          loc.acs.map((vendor) => {
            const k = `${loc.id}|${vendor}`;
            const mode = this.routineState.acs[k] ?? 'disabled';
            return {
              key: k,
              label: vendor,
              locationPrefix: loc.tag,
              isLocked: !this.canManageLocation(loc.id),
              currentMode: mode,
              modeLabel: this.modeLabelFor(mode),
              selectedModeOption: this.selectedOptionFor(mode),
            };
          }),
        );
        const subItems = items.map((item, i) => ({ ...item, isLast: i === items.length - 1 }));
        return { ...step, iconBg: this.iconBgFor(step.tone), isLocked: this.isReadOnly, subItems };
      }

      const mode = this.routineState[step.id as 'blocklist' | 'forceSignout'] ?? 'disabled';
      return {
        ...step,
        iconBg: this.iconBgFor(step.tone),
        isLocked: !this.isGlobal,
        currentMode: mode,
        modeLabel: this.modeLabelFor(mode),
        selectedModeOption: this.selectedOptionFor(mode),
      };
    }).filter((step) => {
      // Org (company-scope) steps: show whenever the caller has company-read.
      // `visit global hris-dashboard` = `hris_lifecycle.routine.read` at company scope.
      // Row is rendered as a locked/read-only badge for read-only viewers, and as
      // a PowerSelect for company-manage callers via the existing isLocked/isReadOnly
      // wiring on the row. Backend `filterByReadScope` already drops org steps for
      // callers without company-read, so this filter matches the data shape.
      if (step.kind === 'org') return this.abilities.can('visit global hris-dashboard');
      return 'subItems' in step && step.subItems.length > 0;
    });
  }

  @action scrollToModes(): void {
    document.getElementById('about-modes')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  @action setEventId(id: string): void {
    this.eventId = id;
  }

  @action setGraceQty(event: Event): void {
    const input = event.target as HTMLInputElement;
    const val = parseInt(input.value || '0', 10);
    this.gracePeriodQty = Math.max(0, isNaN(val) ? 0 : val);
  }

  @action setGraceUnit(option: { value: string; label: string }): void {
    this.gracePeriodUnit = option.value as 'minutes' | 'hours' | 'days';
  }

  @action setMode(stepId: string, key: string | null, option: { value: Mode; label: string }): void {
    const s = this.routineState;
    const mode = option.value;
    if (stepId === 'acs' && key) {
      this.routineState = { ...s, acs: { ...s.acs, [key]: mode } };
    } else {
      this.routineState = { ...s, [stepId]: mode };
    }
  }

  @action async save(): Promise<void> {
    if (this.graceInvalid) return;
    const groups = this.computeGoingLive();
    if (groups.length) {
      this.pendingDiffGroups = groups;
      this.showSaveConfirmModal = true;
      return;
    }
    await this._commitSave();
  }

  @action async confirmSave(): Promise<void> {
    this.showSaveConfirmModal = false;
    this.pendingDiffGroups = [];
    await this._commitSave();
  }

  @action cancelSave(): void {
    this.showSaveConfirmModal = false;
    this.pendingDiffGroups = [];
  }

  @action cancel(): void {
    this.gracePeriodQty = this._savedGraceQty;
    this.gracePeriodUnit = this._savedGraceUnit;
    this.routineState = cloneState(this._savedState);
  }

  private async _commitSave(): Promise<void> {
    const multipliers = { minutes: 60, hours: 3600, days: 86400 };
    const gracePeriodSeconds = this.gracePeriodQty * multipliers[this.gracePeriodUnit];

    const steps: Array<Record<string, unknown>> = [];

    if (Object.keys(this.routineState.acs).length) {
      steps.push({
        step_type: 'LOCATION_ACS_DEPROVISION',
        order: 100,
        per_integration_location: Object.entries(this.routineState.acs).map(([key, mode]) => {
          const [locationId, integrationType] = key.split('|');
          return { location_id: Number(locationId), integration_type: integrationType, mode: toApiMode(mode) };
        }),
      });
    }

    steps.push({ step_type: 'COMPANY_BLOCKLIST_ADD', order: 200, mode: toApiMode(this.routineState.blocklist) });
    steps.push({ step_type: 'COMPANY_FORCE_SIGNOUT', order: 300, mode: toApiMode(this.routineState.forceSignout) });

    const companyId = this.state.currentCompany?.id;
    let response: { rejected_entries?: Array<{ step_type: string; location_id: number | null; reason: string }> };
    try {
      response = await this.ajaxFetch.request(
        `${config.hrisLifecycleApiHost}/a/hris-lifecycle/api/v1/customer-routine-snapshots?company_id=${companyId}&lifecycle_event_type=TERMINATION`,
        {
          method: 'POST',
          data: { schema_version: 2, grace_period_seconds: gracePeriodSeconds, steps },
          contentType: 'application/json',
        },
      );
    } catch (err) {
      if (err instanceof ForbiddenError) {
        this.flashMessages.showAndHideFlash('error', 'You are not authorized to save this configuration.');
      } else {
        this.flashMessages.showAndHideFlash('error', 'Failed to save configuration. Please try again.');
      }
      return;
    }

    const rejected = response.rejected_entries ?? [];
    if (rejected.length === 0) {
      this.flashMessages.showAndHideFlash('success', 'Configuration saved.');
    } else {
      this.flashMessages.showAndHideFlash(
        'warning',
        'Configuration partially saved.',
        `${rejected.length} change${rejected.length === 1 ? '' : 's'} could not be applied — you may not have permission to edit those entries.`,
      );
    }

    this._savedGraceQty = this.gracePeriodQty;
    this._savedGraceUnit = this.gracePeriodUnit;
    this._savedState = cloneState(this.routineState);
    this.routineState = { ...this.routineState };
  }
}
